import React, {useEffect} from 'react';
import {Switch, Route, useLocation} from 'react-router-dom';
import {connect} from 'react-redux';
import Drawer from './utils/Drawer/Drawer';
import SidePanel from './utils/SidePanel/SidePanel';
import Overview from './Overview/Overview';
import Orders from './Orders/Orders';
import Analytics from './Analytics/Analytics';
import Intercom from 'screens/Dashboard/utils/Intercom';
import {ISupplierUserProfile} from 'constants/interfaces';
import {PermissionsService} from 'services/PermissionsService';
import Customers from 'screens/Dashboard/Customers/Customers';
import Company from 'screens/Dashboard/Company/Company';
import Products from 'screens/Dashboard/Products/Products';
import Invite from 'screens/Dashboard/InviteSupplier/InviteSupplier';
import Quotes from 'screens/Dashboard/Quotes/Quotes';
import Team from './Team/Team';
import Inventory from 'screens/Dashboard/Inventory/Inventory';
import PurchaseOrders from 'screens/Dashboard/PurchaseOrders/PurchaseOrders';
import OrderBundles from 'screens/Dashboard/OrderBundles/OrderBundles';
import Programs from 'screens/Dashboard/Programs/Programs';
import CustomerDashboard from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboard/CustomerDashboard';
import Applications from 'screens/Dashboard/Applications/Applications';

// import FundingApplications from 'screens/Dashboard/Customers/CustomerDetail/FunderApplications/FunderApplications';
import AIBookkeeper from 'screens/Dashboard/Customers/CustomerDetail/AIBookkeeper/AIBookkeeper';

import './Dashboard.scss';
import ApplicationDetail from 'screens/Dashboard/Applications/ApplicationDetail/ApplicationDetail';
import Contracts from './Contracts/Contracts';
// eslint-disable-next-line max-len
import CustomerDashboardMapping from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/CustomerDashboardMapping';

import WalletScreen from 'screens/Dashboard/Customers/CustomerDetail/WalletScreen/WalletScreen';
import ProgramPage from 'screens/Dashboard/Programs/ProgramCreate/ProgramPage';
import Invoices from './Invoices/Invoices';
import Groups from 'screens/Dashboard/Groups/Groups';
import GroupDetailPage from 'screens/Dashboard/Groups/GroupDetail/GroupDetailWrapper';

type DashboardProps = {
  history?: any;
  user: ISupplierUserProfile;
  productState?: any;
  createProgramState?: any;
};

class Dashboard extends React.Component<DashboardProps> {
  state = {
    isDrawerOpen: false,
  };

  openDrawer() {
    this.setState({isDrawerOpen: true});
  }

  closeDrawer() {
    this.setState({isDrawerOpen: false});
  }

  render() {
    const permissionsService = new PermissionsService(this.props.user);
    const pathname = window.location.pathname;

    // 1) Detect if user is on the '/mapping' route
    const isMappingRoute = pathname.includes('/mapping');

    // Detect the single-group detail view ('/groups/:groupId'), but not the
    // '/groups' list route itself.
    const isGroupDetailRoute = /^\/groups\/[^/]+$/.test(pathname);

    // 2) Also handle minimized logic for your existing design
    const isMinimized =
      pathname.startsWith('/customers/dashboard/') ||
      pathname.startsWith('/applications/');

    return (
      <div className="dashboard-container roboto">
        <Intercom />

        {/*
          3) If isMappingRoute === true, add the 'no-sidebars' class
             so we hide Drawer and SidePanel in the layout (full-width).
             If isGroupDetailRoute === true, add 'no-left-sidebar' so we
             only hide the Drawer (the right SidePanel stays as-is).
        */}
        <div
          className={`body-section ${isMappingRoute ? 'no-sidebars' : ''} ${
            isGroupDetailRoute ? 'no-left-sidebar' : ''
          }`}
        >
          {/* Drawer on the left, hidden when mapping or viewing a group */}
          {!isMappingRoute && !isGroupDetailRoute && (
            <div className="drawer-section open">
              <Drawer
                isDrawerOpen={this.state.isDrawerOpen}
                onDrawerClose={() => this.closeDrawer()}
                isMinimized={isMinimized}
              />
              <LocationListener onLocationChange={() => this.closeDrawer()} />
            </div>
          )}

          {/* Main content area */}
          <div
            className={`content-section open ${isMinimized ? 'minimized' : ''}`}
          >
            <div className="main-content-container">
              <Switch>
                <Route path="/" exact component={Overview} />
                {permissionsService.shouldShowAnalytics() && (
                  <Route path="/analytics" exact component={Analytics} />
                )}
                {permissionsService.shouldShowCustomers() && (
                  <Route path="/customers" exact component={Customers} />
                )}
                {permissionsService.shouldShowFundingApplications() && (
                  <Route path="/applications" exact component={Applications} />
                )}
                {permissionsService.shouldShowFundingApplications() && (
                  <Route
                    path="/applications/:applicationId"
                    exact
                    component={ApplicationDetail}
                  />
                )}
                {permissionsService.shouldShowCustomers() && (
                  <Route
                    path="/customers/dashboard/:customerUserId"
                    exact
                    component={CustomerDashboard}
                  />
                )}
                {permissionsService.shouldShowCustomers() && (
                  <Route
                    path="/customers/dashboard/:customerUserId/mapping"
                    exact
                    component={CustomerDashboardMapping}
                  />
                )}
                {permissionsService.shouldShowCustomers() && (
                  <Route
                    path="/contracts/:customerUserId"
                    exact
                    component={Contracts}
                  />
                )}

                {/* AI Bookkeeper */}
                {permissionsService.shouldShowFundingApplications() && (
                  <Route
                    path="/customers/dashboard/:customerUserId/bookkeeping-ai"
                    exact
                    component={AIBookkeeper}
                  />
                )}
                {permissionsService.shouldShowProducts() && (
                  <Route path="/products" exact component={Products} />
                )}
                {permissionsService.shouldShowInventory() && (
                  <Route path="/inventory" exact component={Inventory} />
                )}
                {permissionsService.shouldShowQuotes() && (
                  <Route path="/quotes" exact component={Quotes} />
                )}
                {permissionsService.shouldShowOrders() && (
                  <Route path="/orders" exact component={Orders} />
                )}
                {permissionsService.shouldShowOrders() && (
                  <Route path="/invoices" exact component={Invoices} />
                )}
                {permissionsService.shouldShowPurchaseOrders() && (
                  <Route
                    path="/purchase-orders"
                    exact
                    component={PurchaseOrders}
                  />
                )}

                {permissionsService.shouldShowPrograms() && (
                  <Route path="/programs" exact component={Programs} />
                )}
                {permissionsService.shouldShowPrograms() && (
                  <Route
                    path="/programs/create"
                    exact
                    component={ProgramPage}
                  />
                )}
                {permissionsService.shouldShowPrograms() && (
                  <Route path="/programs/:id" exact component={ProgramPage} />
                )}
                {!permissionsService.shouldShowCompany() && (
                  <Route path="/team" component={Team} />
                )}
                {permissionsService.shouldShowCompany() && (
                  <Route path="/company" component={Company} />
                )}
                {permissionsService.shouldShowOnboard() && (
                  <Route path="/onboard" exact component={Invite} />
                )}
                {permissionsService.shouldShowOrderBundles() && (
                  <Route path="/order-bundles" exact component={OrderBundles} />
                )}
                {/* Route for the wallet screen */}
                {permissionsService.shouldShowCustomers() && (
                  <Route
                    path="/customers/dashboard/:customerUserId/wallets/:walletId"
                    exact
                    component={WalletScreen}
                  />
                )}
                {permissionsService.shouldShowGroups() && (
                  <Route path="/groups" exact component={Groups} />
                )}
                {permissionsService.shouldShowGroups() && (
                  <Route
                    path="/groups/:groupId"
                    exact
                    component={GroupDetailPage}
                  />
                )}
              </Switch>
            </div>
          </div>

          {/* Right side panel, hidden when mapping */}
          {!isMappingRoute && (
            <div className="right-sidebar-section">
              <SidePanel />
            </div>
          )}
        </div>
      </div>
    );
  }
}

function LocationListener(props: {onLocationChange: VoidFunction}) {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname) {
      props.onLocationChange();
    }
  }, [location.pathname]);
  return null;
}

const mapStateToProps = (state: any) => ({
  user: state.user,
  productState: state.products,
  createProgramState: state.createProgram,
});

export default connect(mapStateToProps)(Dashboard);
